import { captureException, init as initSentry } from '@sentry/browser';

type LogLevel = 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  data?: unknown;
}

class Logger {
  private static instance: Logger;
  private logs: LogEntry[] = [];
  private readonly maxLogs = 1000;
  private initialized = false;

  private constructor() {
    if (process.env.NODE_ENV === 'production' && import.meta.env.VITE_SENTRY_DSN) {
      initSentry({
        dsn: import.meta.env.VITE_SENTRY_DSN,
        environment: import.meta.env.MODE,
        tracesSampleRate: 1.0,
      });
      this.initialized = true;
    }
  }

  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger();
    }
    return Logger.instance;
  }

  private log(level: LogLevel, message: string, data?: unknown) {
    const entry: LogEntry = {
      level,
      message,
      timestamp: new Date().toISOString(),
      data,
    };

    this.logs.push(entry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    if (process.env.NODE_ENV === 'development') {
      console[level](message, data);
    }

    // En production, envoi à Sentry
    if (process.env.NODE_ENV === 'production' && this.initialized) {
      if (level === 'error') {
        if (data instanceof Error) {
          captureException(data);
        } else {
          captureException(new Error(message));
        }
      }
    }
  }

  info(message: string, data?: unknown) {
    this.log('info', message, data);
  }

  warn(message: string, data?: unknown) {
    this.log('warn', message, data);
  }

  error(message: string, data?: unknown) {
    this.log('error', message, data);
  }

  getLogs(): LogEntry[] {
    return [...this.logs];
  }

  // Nouvelle méthode pour les métriques de performance
  logPerformance(metric: {
    name: string;
    duration: number;
    data?: Record<string, unknown>;
  }) {
    this.info(`Performance: ${metric.name}`, {
      duration: metric.duration,
      ...metric.data,
    });
  }
}

export const logger = Logger.getInstance();